import { resolveCorrectOrder } from "@memorang/helpers/ordering";
import type {
	Answer,
	AnsweredFormState,
	BaseItemSchema,
	Choice,
	Grading,
	ItemResponseFormField,
} from "@memorang/types";
import { getAnsweredFormStates } from "../containers/StudySessionBottombarContainer/answer-correctness";
import { isBlankValuesResponse } from "./ai-feedback";
import { extractBlankIds } from "./blank-tokens";
import {
	getIntlExplanationForChoice,
	getResolvedCorrectAnswerIds,
} from "./session";
import { isTextAnswerMatch } from "./text-match";

export const NOT_ANSWERED = "(not answered)";
export const NOT_AVAILABLE = "(not available)";

export type ResolvedSelection = {
	text?: string;
	stem?: string;
	isCorrect?: boolean;
};

export type OriginalQuestionResolution = {
	stem: string;
	choices: { text?: string; isCorrect?: boolean }[];
	selectedChoice: ResolvedSelection[];
	correctChoice: string;
	incorrectChoices: string[];
};

export type BuildOriginalQuestionParams = {
	item: BaseItemSchema;
	answer?: Answer;
	itemState?: { selectedChoiceIds?: string[]; currentOrders?: string[] };
	formStates?: Record<string, string>;
	liveResponse?: unknown;
};

type LabelledEntry = {
	label: string;
	text?: string;
	isCorrect?: boolean;
};

export const getSelectionTexts = (selections: ResolvedSelection[]): string[] =>
	selections.flatMap((selection) => (selection.text ? [selection.text] : []));

type HighlightResponse = Extract<
	BaseItemSchema["response"],
	{ type: "INLINE_TEXT_SELECTION" }
>;

export const highlightWordChoices = (
	incorrectWords: HighlightResponse["incorrectWords"],
): Choice[] =>
	(incorrectWords ?? []).map((word) => ({
		id: `word-${word.wordIndex}`,
		text: word.transcriptWord,
	}));

const quote = (text: string | undefined, fallback: string) =>
	text ? `"${text}"` : fallback;

const toSelections = (entries: LabelledEntry[]): ResolvedSelection[] =>
	entries.map(({ label, text, isCorrect }) => {
		const rendered = `${label}: ${quote(text, NOT_ANSWERED)}`;
		return { text: rendered, stem: rendered, isCorrect };
	});

const joinEntries = (entries: LabelledEntry[]) =>
	entries
		.map(({ label, text }) => `${label}: ${quote(text, NOT_AVAILABLE)}`)
		.join("; ");

const unansweredSelection = (): ResolvedSelection[] => [
	{ text: NOT_ANSWERED, stem: NOT_ANSWERED },
];

const resolveStem = ({ content }: BaseItemSchema): string => {
	const stemText = content?.stem?.text;
	if (stemText) {
		return stemText;
	}
	const stimulus = content?.stimulus;
	if (stimulus?.type === "TEXT" || stimulus?.type === "text") {
		return stimulus.text ?? "";
	}
	return "";
};

const formStateResponses = (
	states?: Record<string, AnsweredFormState>,
): Record<string, string> | undefined => {
	const entries = Object.entries(states ?? {}).flatMap(([id, state]) =>
		state?.response ? [[id, state.response] as const] : [],
	);
	return entries.length ? Object.fromEntries(entries) : undefined;
};

const resolveBlankValues = ({
	answer,
	liveResponse,
	formStates,
}: BuildOriginalQuestionParams): Record<string, string> | undefined =>
	answer?.selectedBlankValues ??
	formStateResponses(answer?.answeredFormStates) ??
	(isBlankValuesResponse(liveResponse) ? liveResponse : undefined) ??
	formStates;

const blankAnswerKey = (
	scoring?: Grading,
): Record<string, string> | undefined => {
	if (
		scoring?.type !== "ANSWER_KEY_MAPPING" &&
		scoring?.type !== "ANSWER_KEY_TEXT"
	) {
		return undefined;
	}
	const map: Record<string, string> = {};
	for (const entry of scoring.answerKey) {
		if (!("blankId" in entry)) {
			continue;
		}
		if ("word" in entry) {
			map[entry.blankId] = entry.word;
		} else {
			map[entry.blankId] = "wordId" in entry ? entry.wordId : entry.choiceId;
		}
	}
	return map;
};

const orderedBlankIds = (
	declared: string[],
	answerKey?: Record<string, string>,
	values?: Record<string, string>,
): string[] =>
	declared.length ? declared : Object.keys(answerKey ?? values ?? {});

const buildFromChoices = (
	choices: Choice[],
	correctAnswerIds: string[],
	selectedChoiceIds: string[],
): Omit<OriginalQuestionResolution, "stem"> => {
	const markedChoices = choices.map((choice) => ({
		text: choice.text,
		isCorrect: correctAnswerIds.includes(choice.id),
	}));
	const selectedChoice = selectedChoiceIds.length
		? selectedChoiceIds.map((choiceId) => {
				const choice = choices.find((c) => c.id === choiceId);
				return {
					text: choice?.text,
					stem: choice?.text,
					isCorrect: correctAnswerIds.includes(choiceId),
				};
			})
		: unansweredSelection();
	// Without correctness data every choice would land in incorrectChoices,
	// telling the assistant the true answer is a distractor.
	const hasCorrectness = correctAnswerIds.length > 0;
	return {
		choices: markedChoices,
		selectedChoice,
		correctChoice: hasCorrectness
			? markedChoices
					.filter((c) => c.isCorrect)
					.map((c) => c.text ?? "")
					.join("; ")
			: NOT_AVAILABLE,
		incorrectChoices: hasCorrectness
			? markedChoices.filter((c) => !c.isCorrect).map((c) => c.text ?? "")
			: [],
	};
};

// Correctness is unknown (undefined) when there is no reference answer to
// compare against; an empty value is never correct, so `"" === ""` cannot
// mark an unanswered blank right.
const gradeAgainst = (
	value: string | undefined,
	reference: string | undefined,
	matches: (value: string, reference: string) => boolean,
): boolean | undefined => {
	if (reference == null || reference === "") {
		return undefined;
	}
	return value ? matches(value, reference) : false;
};

const buildBlankResolution = (
	blankIds: string[],
	values: Record<string, string> | undefined,
	displayText: (blankId: string, valueId: string) => string | undefined,
	correctText: (blankId: string) => string | undefined,
	isBlankCorrect: (blankId: string, value?: string) => boolean | undefined,
	optionEntries: LabelledEntry[] = [],
): Omit<OriginalQuestionResolution, "stem"> => {
	const selectedEntries = blankIds.map((blankId) => {
		const value = values?.[blankId];
		return {
			label: blankId,
			text: value ? displayText(blankId, value) : undefined,
			isCorrect: isBlankCorrect(blankId, value),
		};
	});
	const correctEntries = blankIds.map((blankId) => ({
		label: blankId,
		text: correctText(blankId),
	}));
	return {
		choices: optionEntries.map(({ label, text, isCorrect }) => ({
			text: `${label}: ${quote(text, NOT_AVAILABLE)}`,
			isCorrect,
		})),
		selectedChoice: selectedEntries.length
			? toSelections(selectedEntries)
			: unansweredSelection(),
		correctChoice: joinEntries(correctEntries),
		incorrectChoices: optionEntries
			.filter((entry) => entry.isCorrect === false)
			.map(({ label, text }) => `${label}: ${quote(text, NOT_AVAILABLE)}`),
	};
};

const buildOrdering = (
	segments: Choice[],
	learnerOrder: string[] | undefined,
	correctOrder: string[],
): Omit<OriginalQuestionResolution, "stem"> => {
	const textOf = (id: string) => segments.find((s) => s.id === id)?.text;
	const label = (index: number) => `position_${index + 1}`;
	const placed = learnerOrder ?? [];
	const positionCount = Math.max(placed.length, correctOrder.length);
	const selectedEntries = Array.from({ length: positionCount }, (_, index) => {
		const id = placed[index];
		return {
			label: label(index),
			text: id ? textOf(id) : undefined,
			isCorrect: correctOrder.length
				? Boolean(id) && correctOrder[index] === id
				: undefined,
		};
	});
	return {
		choices: [],
		selectedChoice: selectedEntries.length
			? toSelections(selectedEntries)
			: unansweredSelection(),
		correctChoice: correctOrder.length
			? joinEntries(
					correctOrder.map((id, index) => ({
						label: label(index),
						text: textOf(id),
					})),
				)
			: NOT_AVAILABLE,
		incorrectChoices: [],
	};
};

const buildForm = (
	fields: ItemResponseFormField[],
	answeredFormStates: Record<string, AnsweredFormState> | undefined,
	formStates: Record<string, string> | undefined,
): Omit<OriginalQuestionResolution, "stem"> => {
	const states =
		answeredFormStates ??
		(formStates ? getAnsweredFormStates(fields, formStates) : undefined);
	const selectedEntries = fields.map((field) => ({
		label: field.id,
		text: states?.[field.id]?.response,
		isCorrect: states?.[field.id]?.isCorrect,
	}));
	return {
		choices: [],
		selectedChoice: selectedEntries.length
			? toSelections(selectedEntries)
			: unansweredSelection(),
		correctChoice: joinEntries(
			fields.map((field) => ({
				label: field.id,
				text: field.expectedResponse?.[0] ?? field.expected_response,
			})),
		),
		incorrectChoices: [],
	};
};

const isChoiceResponse = (
	response: BaseItemSchema["response"],
): response is Extract<
	BaseItemSchema["response"],
	{ type: "choice" | "OPTION_SELECTION" }
> => response.type === "choice" || response.type === "OPTION_SELECTION";

const getCorrectChoice = ({
	response,
	scoring,
}: BaseItemSchema): Choice | undefined => {
	if (!isChoiceResponse(response)) {
		return undefined;
	}
	const correctAnswerIds = getResolvedCorrectAnswerIds(
		response.choices,
		scoring,
	);
	return response.choices.find((choice) =>
		correctAnswerIds.includes(choice.id),
	);
};

export const resolveChoiceExplanation = (item: BaseItemSchema): string => {
	const correctChoice = getCorrectChoice(item);
	return correctChoice
		? (getIntlExplanationForChoice(correctChoice) ?? "")
		: "";
};

type BranchResolution = Omit<OriginalQuestionResolution, "stem"> | undefined;
type BranchResolver = (
	params: BuildOriginalQuestionParams,
	stem: string,
) => BranchResolution;

const resolveSelectedChoiceIds = ({
	answer,
	itemState,
}: BuildOriginalQuestionParams) =>
	answer?.selectedChoiceIds ?? itemState?.selectedChoiceIds ?? [];

const resolveChoiceLike: BranchResolver = (params) => {
	const { response, scoring } = params.item;
	if (!isChoiceResponse(response)) {
		return undefined;
	}
	const { choices } = response;
	return buildFromChoices(
		choices,
		getResolvedCorrectAnswerIds(choices, scoring),
		resolveSelectedChoiceIds(params),
	);
};

const resolveHighlight: BranchResolver = (params) => {
	const { response, scoring } = params.item;
	if (response.type !== "INLINE_TEXT_SELECTION") {
		return undefined;
	}
	const wordChoices = highlightWordChoices(response.incorrectWords);
	const correctAnswerIds = getResolvedCorrectAnswerIds(wordChoices, scoring);
	const { selectedChoice } = buildFromChoices(
		wordChoices,
		correctAnswerIds,
		resolveSelectedChoiceIds(params),
	);
	const correctWords = wordChoices
		.filter((word) => correctAnswerIds.includes(word.id))
		.map((word) => word.text);
	return {
		choices: [],
		selectedChoice,
		incorrectChoices: [],
		correctChoice: correctWords.length
			? correctWords.join("; ")
			: NOT_AVAILABLE,
	};
};

const resolveDropdownBlanks: BranchResolver = (params) => {
	const { response, scoring } = params.item;
	if (
		response.type !== "INLINE_OPTION_SELECTION" &&
		response.type !== "BLANK_DROPDOWN"
	) {
		return undefined;
	}
	const answerKey = blankAnswerKey(scoring);
	const choicesFor = (blankId: string) =>
		response.blanks.find((blank) => blank.blankId === blankId)?.choices ?? [];
	const correctIdFor = (blankId: string): string | undefined =>
		answerKey?.[blankId] ??
		choicesFor(blankId).find((choice) => choice.isCorrect)?.id;
	const optionIsCorrect = (blankId: string, choiceId: string) => {
		const correctId = correctIdFor(blankId);
		return correctId == null ? undefined : choiceId === correctId;
	};
	return buildBlankResolution(
		response.blanks.map((blank) => blank.blankId),
		resolveBlankValues(params),
		(blankId, valueId) =>
			choicesFor(blankId).find((choice) => choice.id === valueId)?.text,
		(blankId) =>
			choicesFor(blankId).find((choice) => choice.id === correctIdFor(blankId))
				?.text,
		(blankId, value) =>
			gradeAgainst(value, correctIdFor(blankId), (a, b) => a === b),
		response.blanks.flatMap((blank) =>
			(blank.choices ?? []).map((choice) => ({
				label: blank.blankId,
				text: choice.text,
				isCorrect: optionIsCorrect(blank.blankId, choice.id),
			})),
		),
	);
};

const resolveDragDropBlanks: BranchResolver = (params, stem) => {
	const { response, scoring } = params.item;
	if (response.type !== "TOKEN_MAPPING") {
		return undefined;
	}
	const values = resolveBlankValues(params);
	const answerKey = blankAnswerKey(scoring);
	const textOf = (wordId?: string) =>
		response.wordBank.find((word) => word.id === wordId)?.text;
	const correctWordIds = new Set(Object.values(answerKey ?? {}));
	return buildBlankResolution(
		orderedBlankIds(extractBlankIds(stem), answerKey, values),
		values,
		(_blankId, valueId) => textOf(valueId),
		(blankId) => textOf(answerKey?.[blankId]),
		(blankId, value) =>
			gradeAgainst(value, answerKey?.[blankId], (a, b) => a === b),
		response.wordBank.map((word) => ({
			label: "word_bank",
			text: word.text,
			isCorrect: answerKey ? correctWordIds.has(word.id) : undefined,
		})),
	);
};

const resolveTypeInBlanks: BranchResolver = (params, stem) => {
	const { response, scoring } = params.item;
	if (response.type !== "SHORT_TEXT_ENTRY") {
		return undefined;
	}
	const values = resolveBlankValues(params);
	const answerKey = blankAnswerKey(scoring);
	return buildBlankResolution(
		orderedBlankIds(extractBlankIds(stem), answerKey, values),
		values,
		(_blankId, value) => value,
		(blankId) => answerKey?.[blankId],
		(blankId, value) =>
			gradeAgainst(value, answerKey?.[blankId], isTextAnswerMatch),
	);
};

const resolveOrdering: BranchResolver = (params) => {
	const { answer, itemState, liveResponse, item } = params;
	const { response, scoring, content } = item;
	if (response.type !== "ORDER_RECONSTRUCTION" && response.type !== "reorder") {
		return undefined;
	}
	const segments: Choice[] =
		response.type === "reorder"
			? (response.choices ?? [])
			: (content?.stimulus?.segments ?? []).map((segment) => ({
					id: String(segment.id),
					text: segment.text,
				}));
	let correctOrder: string[] = [];
	if (scoring?.type === "ANSWER_KEY_ORDERING") {
		correctOrder =
			resolveCorrectOrder(scoring.answerKey, scoring.correctOrder) ?? [];
	} else if (response.type === "reorder") {
		correctOrder = (response.correctResponse ?? []).map((entry) => entry.id);
	}
	const liveOrder = Array.isArray(liveResponse)
		? liveResponse.map(String)
		: undefined;
	return buildOrdering(
		segments,
		answer?.selectedOrder ?? liveOrder ?? itemState?.currentOrders,
		correctOrder,
	);
};

const resolveNoteCompletion: BranchResolver = ({
	item,
	answer,
	formStates,
}) => {
	if (item.response.type !== "form") {
		return undefined;
	}
	return buildForm(
		item.response.fields ?? [],
		answer?.answeredFormStates,
		formStates,
	);
};

const BRANCH_RESOLVERS: BranchResolver[] = [
	resolveChoiceLike,
	resolveHighlight,
	resolveDropdownBlanks,
	resolveDragDropBlanks,
	resolveTypeInBlanks,
	resolveOrdering,
	resolveNoteCompletion,
];

export const buildOriginalQuestionData = (
	params: BuildOriginalQuestionParams,
): OriginalQuestionResolution => {
	const stem = resolveStem(params.item);
	for (const resolve of BRANCH_RESOLVERS) {
		const resolution = resolve(params, stem);
		if (resolution) {
			return { stem, ...resolution };
		}
	}
	return {
		stem,
		choices: [],
		selectedChoice: [],
		correctChoice: "",
		incorrectChoices: [],
	};
};
